<div class="qibla-page">

  <!-- Hero Banner -->
  <div class="qibla-hero-wrap">
    <section class="qibla-hero">
      <div class="qibla-hero__bg"></div>
      <div class="qibla-hero__content">
        <span class="qibla-hero__ornament" aria-hidden="true">✦ ☾ ✦</span>
        <h1 class="qibla-hero__title">{{ 'pages.qibla_finder.title' | translate }}</h1>
        <p class="qibla-hero__tagline">{{ 'pages.qibla_finder.subtitle' | translate }}</p>
        <button type="button" class="qibla-hero__location">📍 {{ locationName }} ▾</button>
      </div>
    </section>

    <div class="qibla-hero__distance-card">
      <span class="qibla-hero__card-label">Distance to Kaaba</span>
      <span class="qibla-hero__card-val">{{ distanceToMakkah }}</span>
      <span class="qibla-hero__card-sub">from your location</span>
      <span class="qibla-hero__card-coords">{{ locationCoords }}</span>
    </div>

    <div class="qibla-hero__angle-card">
      <span class="qibla-hero__card-label">Qibla Angle</span>
      <span class="qibla-hero__card-val">{{ qiblaBearing }}°</span>
      <span class="qibla-hero__card-sub">from North</span>
      <span class="qibla-hero__card-updated">🕐 {{ lastUpdated }}</span>
    </div>
  </div>

  <!-- Page body -->
  <div class="qibla-page__body">

    <!-- Main: compass section + sidebar -->
    <div class="qibla-page__main">

      <!-- Compass section (3 columns) -->
      <div class="qibla-page__compass-section">

        <!-- Qibla direction card -->
        <div class="qibla-dir-card">
          <div class="qibla-dir-card__kaaba">🕋</div>
          <div class="qibla-dir-card__title">Qibla Direction</div>
          <p class="qibla-dir-card__desc">This is the direction of the Holy Kaaba.</p>
          <div class="qibla-dir-card__ornament">❧</div>
        </div>

        <!-- Compass -->
        <div class="compass-wrap">
          <div class="compass">
            <div class="compass__ring"></div>

            @for (i of [].constructor(tickCount); track $index) {
              <div class="compass__tick"
                   [class.compass__tick--major]="$index % 9 === 0"
                   [style.transform]="'rotate(' + ($index * (360 / tickCount)) + 'deg)'">
              </div>
            }

            @for (pt of compassPoints; track pt.label) {
              <div class="compass__label"
                   [class.compass__label--north]="pt.label === 'N'"
                   [class.compass__label--cardinal]="pt.label.length === 1"
                   [style.transform]="'rotate(' + pt.deg + 'deg) translateY(-112px) rotate(' + (-pt.deg) + 'deg)'">
                {{ pt.label }}
              </div>
            }

            <div class="compass__hub">🕌</div>
          </div>

          <div class="compass__needle-wrap" [style.transform]="'rotate(' + needleRotation + 'deg)'">
            <div class="compass__needle"></div>
            <div class="compass__kaaba">🕋</div>
          </div>
        </div>

        <!-- Action cards -->
        <div class="qibla-actions">

          <div class="qibla-action-card" [class.qibla-action-card--active]="calibrating()" (click)="calibrate()">
            <div class="qibla-action-card__icon">🎯</div>
            <div class="qibla-action-card__body">
              <strong>{{ calibrating() ? ('qibla.calibrating' | translate) : 'Calibrate Compass' }}</strong>
              <span>Improve accuracy by calibrating your device.</span>
              <button type="button" class="qibla-action-card__btn">↺ Calibrate</button>
            </div>
          </div>

          <div class="qibla-action-card">
            <div class="qibla-action-card__icon">📱</div>
            <div class="qibla-action-card__body">
              <strong>AR View</strong>
              <span>See the Qibla direction in your surroundings.</span>
              <button type="button" class="qibla-action-card__btn qibla-action-card__btn--primary">Open AR View</button>
            </div>
          </div>

          <div class="qibla-action-card">
            <div class="qibla-action-card__icon">🔊</div>
            <div class="qibla-action-card__body">
              <strong>Voice Guide</strong>
              <span>Turn on voice to guide you to the Qibla.</span>
              <div class="qibla-toggle" [class.qibla-toggle--on]="voiceEnabled()" (click)="toggleVoice()">
                <div class="qibla-toggle__knob"></div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Sidebar -->
      <div class="qibla-page__sidebar">

        <div class="qibla-sidebar-section">
          <div class="qibla-sidebar-section__header">
            <span>📍 Nearby Mosques</span>
            <span class="qibla-sidebar-section__view-all">View All</span>
          </div>
          @for (mosque of nearbyMosques; track mosque.name) {
            <div class="qibla-mosque-item">
              <div class="qibla-mosque-item__icon">🕌</div>
              <div class="qibla-mosque-item__info">
                <div class="qibla-mosque-item__name">{{ mosque.name }}</div>
                <div class="qibla-mosque-item__loc">{{ mosque.location }}</div>
              </div>
              <div class="qibla-mosque-item__dist">{{ mosque.distance }}</div>
            </div>
          }
        </div>

        <div class="qibla-sidebar-section">
          <div class="qibla-sidebar-section__header">
            <span>🕐 Prayer Times</span>
            <span class="qibla-sidebar-section__view-all">View All</span>
          </div>
          @for (prayer of prayerTimes; track prayer.name) {
            <div class="qibla-prayer-item">
              <span class="qibla-prayer-item__name">{{ prayer.name }}</span>
              <span class="qibla-prayer-item__time">{{ prayer.time }}</span>
            </div>
          }
          <button type="button" class="qibla-sidebar-section__timetable-btn">📅 Full Timetable</button>
        </div>

      </div>
    </div>

    <!-- Instruction bar -->
    <div class="qibla-instruction">
      <div class="qibla-instruction__img">📱</div>
      <p>Hold your device flat and turn it until the green arrow points towards the Kaaba.</p>
    </div>

    <!-- Feature cards -->
    <div class="qibla-features">
      <div class="qibla-feature">
        <div class="qibla-feature__icon">🎯</div>
        <div class="qibla-feature__title">100% Accurate</div>
        <div class="qibla-feature__desc">Uses advanced algorithms to provide precise direction.</div>
      </div>
      <div class="qibla-feature">
        <div class="qibla-feature__icon">📶</div>
        <div class="qibla-feature__title">Works Offline</div>
        <div class="qibla-feature__desc">Find Qibla direction even without internet.</div>
      </div>
      <div class="qibla-feature">
        <div class="qibla-feature__icon">🌐</div>
        <div class="qibla-feature__title">Global Coverage</div>
        <div class="qibla-feature__desc">Works anywhere in the world with great accuracy.</div>
      </div>
      <div class="qibla-feature">
        <div class="qibla-feature__icon">✋</div>
        <div class="qibla-feature__title">Easy to Use</div>
        <div class="qibla-feature__desc">Simple, beautiful and user-friendly interface.</div>
      </div>
    </div>

  </div>
</div>
